import { useCallback, useEffect, useMemo, useRef } from "react";
import useHistory from "routing/useHistory";
import useListKeyboardNavigation from "hooks/useListKeyboardNavigation";
import useActionArguments from "./useActionArguments";
const useQuickJumpKeyboardNavigation = ({ items, onToggle, searchQuery }) => {
  const actionArguments = useActionArguments();
  const history = useHistory();
  const searchInputRef = useRef(null);
  const itemRefs = useRef([]);
  const isClosingRef = useRef(false);
  const skipInitialHoverRef = useRef(false);
  const previousQueryRef = useRef();
  const focusSearchInput = useCallback(() => {
    var _a, _b;
    const node = (_b = (_a = searchInputRef.current) == null ? void 0 : _a.getInputDOMNode) == null ? void 0 : _b.call(_a);
    if (node && document.activeElement !== node) {
      node.focus();
    }
  }, []);
  const onHide = useCallback(() => {
    isClosingRef.current = true;
    onToggle();
  }, [onToggle]);
  const handleItemSelect = useCallback(
    (item) => {
      if ("link" in item) {
        history.push(item.link);
      } else if ("externalLink" in item) {
        window.open(item.externalLink, "_blank", "noopener");
      } else {
        item.action(actionArguments);
      }
      onHide();
    },
    [actionArguments, history, onHide]
  );
  const { highlightedIndex, setHighlightedIndex, onKeyDown } = useListKeyboardNavigation(
    items,
    handleItemSelect
  );
  useEffect(() => {
    isClosingRef.current = false;
    focusSearchInput();
  }, [focusSearchInput]);
  useEffect(() => {
    var _a;
    if (highlightedIndex < 0) {
      return;
    }
    (_a = itemRefs.current[highlightedIndex]) == null ? void 0 : _a.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);
  useEffect(() => {
    const queryChanged = previousQueryRef.current !== searchQuery;
    const listIsEmpty = items.length === 0;
    if (listIsEmpty) {
      setHighlightedIndex(-1);
      skipInitialHoverRef.current = false;
    } else if (queryChanged || highlightedIndex === -1) {
      skipInitialHoverRef.current = false;
      setHighlightedIndex(0);
    }
    previousQueryRef.current = searchQuery;
  }, [highlightedIndex, items, searchQuery, setHighlightedIndex]);
  const getItemProps = useCallback(
    (index) => ({
      tabIndex: -1,
      ref: (node) => {
        itemRefs.current[index] = node;
      },
      onMouseEnter: () => {
        if (!skipInitialHoverRef.current) {
          skipInitialHoverRef.current = true;
        } else {
          setHighlightedIndex(index);
        }
      },
      onFocus: () => {
        setHighlightedIndex(index);
      },
      onClick: () => {
        setHighlightedIndex(index);
        const item = items[index];
        if (item) {
          handleItemSelect(item);
        }
      }
    }),
    [handleItemSelect, items, setHighlightedIndex]
  );
  const searchInputProps = useMemo(
    () => ({
      ref: searchInputRef
    }),
    []
  );
  const handleKeyDownCapture = useCallback(
    (event) => {
      var _a, _b;
      onKeyDown(event);
      if (isClosingRef.current) {
        return;
      }
      const input = (_b = (_a = searchInputRef.current) == null ? void 0 : _a.getInputDOMNode) == null ? void 0 : _b.call(_a);
      if (!input || event.target === input) {
        return;
      }
      if (event.metaKey || event.altKey || event.ctrlKey) {
        return;
      }
      const { key } = event;
      const isCharacter = key.length === 1;
      const isEditingKey = key === "Backspace" || key === "Delete";
      if (!isCharacter && !isEditingKey) {
        return;
      }
      focusSearchInput();
    },
    [focusSearchInput, onKeyDown]
  );
  const modalProps = useMemo(
    () => ({
      onKeyDownCapture: handleKeyDownCapture
    }),
    [handleKeyDownCapture]
  );
  return useMemo(
    () => ({
      highlightedIndex,
      searchInputProps,
      modalProps,
      getItemProps,
      onHide
    }),
    [highlightedIndex, modalProps, searchInputProps, getItemProps, onHide]
  );
};
export default useQuickJumpKeyboardNavigation;
